import { Clock, Database, GitBranch, Target, PhoneCall, MessageSquare, Repeat, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

const Problems = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const problems = [
    {
      icon: Clock,
      title: "Perda de leads por falta de resposta imediata",
      description: "Muitas empresas demoram horas (ou até dias) para responder leads, perdendo oportunidades para concorrentes mais ágeis."
    },
    {
      icon: Database,
      title: "Preenchimento manual de dados no CRM",
      description: "Requer tempo do time e está sujeito a erros humanos."
    },
    {
      icon: GitBranch,
      title: "Integração manual de canais e ferramentas",
      description: "Trabalho dobrado para centralizar informações."
    },
    {
      icon: Target,
      title: "Qualificação lenta e pouco inteligente",
      description: "Leads não são triados corretamente; vendedores gastam tempo em oportunidades ruins."
    },
    {
      icon: PhoneCall,
      title: "Falta de follow-up estruturado",
      description: "Leads esfriam por ausência de contato constante."
    },
    {
      icon: MessageSquare,
      title: "Comunicação inconsistente",
      description: "Falta padronização no discurso e, muitas vezes, o tom da marca não é respeitado."
    },
    {
      icon: Repeat,
      title: "Desperdício de tempo em tarefas repetitivas",
      description: "Profissionais deixam de atuar no estratégico para resolver atividades operacionais."
    }
  ];

  const itemsPerPage = { mobile: 1, tablet: 2, desktop: 3 };
  const totalPages = Math.ceil(problems.length / itemsPerPage.desktop);
  
  const getCurrentItems = () => {
    const startIndex = currentPage * itemsPerPage.desktop;
    return problems.slice(startIndex, startIndex + itemsPerPage.desktop);
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/50 rounded-full text-accent-foreground text-sm font-medium mb-6">
            📊 Diagnóstico de desafios
          </div>
          <h2 className="text-heading text-foreground mb-6">
            Eu imagino que você enfrente os{" "}
            <span className="bg-gradient-hero bg-clip-text text-transparent">
              seguintes problemas
            </span>
          </h2>
          <p className="text-body-large text-muted-foreground max-w-3xl mx-auto">
            Estes são os principais obstáculos que impedem empresas de maximizar suas conversões de vendas
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Controls - Desktop Only */}
          <div className="hidden lg:flex items-center justify-center gap-8 mb-8">
            <Button
              variant="outline"
              size="icon"
              onClick={prevPage}
              disabled={currentPage === 0}
              className="rounded-full"
              aria-label="Página anterior"
            >
              <ChevronLeft size={20} />
            </Button>

            {/* Page Indicators */}
            <div className="flex gap-4">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index)}
                  className="p-2 min-w-6 min-h-6 flex items-center justify-center"
                  aria-label={`Ir para página ${index + 1}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentPage ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  />
                </button>
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={nextPage}
              disabled={currentPage === totalPages - 1}
              className="rounded-full"
              aria-label="Próxima página"
            >
              <ChevronRight size={20} />
            </Button>
          </div>

          {/* Problems Grid - Desktop */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-3 gap-4">
              {getCurrentItems().map((problem, index) => {
                const Icon = problem.icon;
                return (
                  <div
                    key={currentPage * itemsPerPage.desktop + index}
                    className="group bg-gradient-card rounded-2xl p-6 border border-border/50 hover:border-primary/20 transition-all duration-300 hover:shadow-soft animate-fade-in"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-accent/20 rounded-xl group-hover:bg-accent/30 transition-colors duration-300">
                        <Icon size={24} className="text-secondary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-3 leading-tight">
                          {problem.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {problem.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Problems Grid - Mobile/Tablet (All items) */}
          <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4">
            {problems.map((problem, index) => {
              const Icon = problem.icon;
              return (
                <div
                  key={index}
                  className="group bg-gradient-card rounded-2xl p-6 border border-border/50 hover:border-primary/20 transition-all duration-300 hover:shadow-soft"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-accent/20 rounded-xl group-hover:bg-accent/30 transition-colors duration-300">
                      <Icon size={24} className="text-secondary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-3 leading-tight">
                        {problem.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {problem.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-body-large text-muted-foreground mb-6">
            Se você se identificou com estes problemas, nossa IA pode ser a solução que você procura.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Problems;